/**
 * Editor Layers Panel Component
 * Shows the hierarchical structure of page elements with drag & drop reordering
 */
import React, { useState } from "react";
import { useEditor } from "@craftjs/core";
import { Box, IconButton, Typography, Chip, Tooltip } from "@mui/material";
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { componentIcons } from "../editorConstants";

// Sortable Layer Item Component
interface SortableLayerItemProps {
  nodeId: string;
  node: any;
  depth: number;
  isSelected: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  childCount: number;
  onSelect: () => void;
  onToggleExpand: (e: React.MouseEvent) => void;
  onDelete: () => void;
  isRoot: boolean;
  isLinkedNode?: boolean;
}

const SortableLayerItem: React.FC<SortableLayerItemProps> = ({
  nodeId,
  node,
  depth,
  isSelected,
  isExpanded,
  hasChildren,
  childCount,
  onSelect,
  onToggleExpand,
  onDelete,
  isRoot,
  isLinkedNode = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: nodeId, disabled: isRoot });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayName = node.data.displayName || node.data.name || "Element";

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        py: 0.75,
        pr: 1,
        pl: `${depth * 12 + 4}px`,
        cursor: "pointer",
        borderRadius: 2,
        transition: "background-color 0.2s, box-shadow 0.2s",
        bgcolor: isSelected ? "rgba(33, 150, 243, 0.08)" : "transparent",
        color: isSelected ? "primary.main" : "inherit",
        border: isSelected ? "1px solid" : "1px solid transparent",
        borderColor: isSelected ? "rgba(33, 150, 243, 0.3)" : "transparent",
        "&:hover": {
          bgcolor: isSelected ? "rgba(33, 150, 243, 0.12)" : "grey.100",
        },
        "& .action-buttons": {
          opacity: 0,
          transition: "opacity 0.2s",
        },
        "&:hover .action-buttons": {
          opacity: 1,
        },
      }}
    >
      {/* Drag Handle - only for non-root elements */}
      {!isRoot ? (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "grey.400",
            "&:hover": { color: "grey.600" },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIcon sx={{ fontSize: 16 }} />
        </Box>
      ) : (
        <Box sx={{ width: 16 }} />
      )}

      {/* Expand/Collapse button */}
      {hasChildren ? (
        <IconButton
          size="small"
          onClick={onToggleExpand}
          sx={{ width: 20, height: 20, color: "grey.500" }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 14,
              transition: "transform 0.2s",
              transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
        </IconButton>
      ) : (
        <Box sx={{ width: 20 }} />
      )}

      {/* Icon */}
      <Typography sx={{ fontSize: 16, flexShrink: 0 }}>
        {componentIcons[node.data.name] || "📄"}
      </Typography>

      {/* Name */}
      <Typography
        sx={{
          flex: 1,
          fontSize: 14,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: isSelected ? 500 : 400,
        }}
      >
        {displayName}
      </Typography>

      {/* Children count badge */}
      {hasChildren && (
        <Chip
          label={childCount}
          size="small"
          sx={{
            height: 18,
            fontSize: 10,
            bgcolor: "grey.200",
            color: "grey.600",
            mr: 0.5,
          }}
        />
      )}

      {/* Delete button */}
      {!isRoot && (
        <Box className="action-buttons" sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={{
                p: 0.5,
                color: "grey.500",
                "&:hover": { color: "error.main", bgcolor: "error.light" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

// Drag overlay item (shown while dragging)
const DragOverlayItem: React.FC<{ node: any }> = ({ node }) => {
  const displayName = node?.data.displayName || node?.data.name || "Element";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.75,
        px: 2,
        bgcolor: "primary.light",
        borderRadius: 2,
        boxShadow: 3,
        opacity: 0.9,
      }}
    >
      <DragIcon sx={{ fontSize: 16, color: "grey.500" }} />
      <Typography sx={{ fontSize: 16 }}>{componentIcons[node?.data.name] || "📄"}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{displayName}</Typography>
    </Box>
  );
};

export const EditorLayersPanel: React.FC = () => {
  const { nodes, selectedNodeId, actions, query } = useEditor((state) => ({
    nodes: state.nodes,
    selectedNodeId: Array.from(state.events.selected)[0],
  }));

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(["ROOT"]));
  const [activeId, setActiveId] = useState<string | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Toggle expand/collapse
  const toggleExpand = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end - reorder within same parent
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeNode = nodes[active.id as string];
    const overNode = nodes[over.id as string];

    if (!activeNode || !overNode) return;

    // Only allow reordering within the same parent
    const parentId = activeNode.data.parent;
    if (!parentId || activeNode.data.parent !== overNode.data.parent) return;

    const parentNode = nodes[parentId];
    if (!parentNode) return;

    const siblings = parentNode.data.nodes || [];
    const oldIndex = siblings.indexOf(active.id as string);
    const newIndex = siblings.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      actions.move(active.id as string, parentId, newIndex);
    }
  };

  // Get active node for drag overlay
  const activeNode = activeId ? nodes[activeId] : null;

  // Render layer with sortable context for children
  const renderLayer = (nodeId: string, depth: number = 0): React.ReactNode => {
    const node = nodes[nodeId];
    if (!node) return null;

    const children = node.data.nodes || [];
    const isSelected = selectedNodeId === nodeId;
    const isRoot = node.data.name === "RootContainer";
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(nodeId);

    return (
      <Box key={nodeId} sx={{ userSelect: "none" }}>
        {/* Layer item */}
        <SortableLayerItem
          nodeId={nodeId}
          node={node}
          depth={depth}
          isSelected={isSelected}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          childCount={children.length}
          onSelect={() => actions.selectNode(nodeId)}
          onToggleExpand={(e) => toggleExpand(nodeId, e)}
          onDelete={() => {
            // Check linked node
            const parentId = node.data.parent;
            if (parentId && nodes[parentId]) {
              const linkedNodes = nodes[parentId].data.linkedNodes || {};
              if (Object.values(linkedNodes).includes(nodeId)) return;
            }
            if (window.confirm("Delete this element?")) {
              actions.delete(nodeId);
              if (parentId) {
                requestAnimationFrame(() => {
                  actions.selectNode(parentId);
                });
              }
            }
          }}
          isRoot={isRoot}
        />

        {/* Children - with sortable context */}
        {hasChildren && isExpanded && (
          <Box sx={{ position: "relative" }}>
            {/* Tree line */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                borderLeft: "2px solid",
                borderColor: "grey.200",
                ml: `${depth * 12 + 14}px`,
              }}
            />
            <SortableContext items={children} strategy={verticalListSortingStrategy}>
              {children.map((childId: string) => renderLayer(childId, depth + 1))}
            </SortableContext>
          </Box>
        )}
      </Box>
    );
  };

  // Count total elements
  const countElements = (nodeId: string): number => {
    const node = nodes[nodeId];
    if (!node) return 0;
    const children = node.data.nodes || [];
    return 1 + children.reduce((sum: number, id: string) => sum + countElements(id), 0);
  };
  const totalElements = nodes.ROOT ? countElements("ROOT") - 1 : 0; // Exclude ROOT

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header with actions */}
      <Box
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "grey.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {totalElements} elements
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          Drag to reorder
        </Typography>
      </Box>

      {/* Layer tree with DnD context */}
      <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {nodes.ROOT ? (
            renderLayer("ROOT")
          ) : (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No elements yet
              </Typography>
            </Box>
          )}
          <DragOverlay>
            {activeNode ? <DragOverlayItem node={activeNode} /> : null}
          </DragOverlay>
        </DndContext>
      </Box>
    </Box>
  );
};

export default EditorLayersPanel;
